import { useCallback, useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { ChevronLeft, HistoryOutlined } from '@mui/icons-material'
import { Alert, Grid, IconButton, Typography } from '@mui/material'

import DashboardScreen from '../../components/screen/dashboard-screen'
import StickyHeader from '../../components/sticky-header'
import { DashboardContext } from '../../providers/dashboard-provider'
import { NavContext } from '../../providers/nav-provider'
import Api from '../../utils/api'
import useIsMobile from '../../utils/hooks/useIsMobile'

import OrderHistoryContext from './context'
import useStyles from './index.styles'
import OrderCard from './order-card'
import ReturnCard from './return-card'

const OrderHistory = () => {
  const classes = useStyles
  const navigate = useNavigate()
  const { selectedFolderId: folderId } = useContext(DashboardContext)
  const { orderNumber } = useParams<{ folderId: string; orderNumber: string }>()
  const isMobile = useIsMobile()
  const { setMoreMenuOpen } = useContext(NavContext)

  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<boolean>(false)
  const [orders, setOrders] = useState<OrderTrackingItem[]>([])
  const [returns, setReturns] = useState<ReturnItem[]>([])
  const [selectedOrder, setSelectedOrder] = useState<OrderTrackingItem | undefined>(undefined)

  const hasMultipleFolders = new Set(orders.map((o) => o.folderId)).size > 1

  const fetchHistory = useCallback(async () => {
    if (!folderId) {
      setError(true)
      return
    }
    setError(false)

    setLoading(true)
    const res = await Api.get(`/api/v2/protect/folders/${folderId}/order-history`)
    if (res.ok) {
      setLoading(false)
      const newOrders: OrderTrackingItem[] = []
      res.body.orders.forEach((order: OrderItem) =>
        order.trackingUrls.length > 0
          ? order.trackingUrls.forEach((url) => newOrders.push({ ...order, trackingUrl: url }))
          : newOrders.push({ ...order, trackingUrl: undefined })
      )
      newOrders.sort((a, b) => new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime())
      setOrders(newOrders)
      setReturns(res.body.returns)
    } else {
      setLoading(false)
      setError(true)
    }
  }, [folderId])

  useEffect(() => {
    fetchHistory()
  }, [fetchHistory])

  // Select the order if the orderNumber is present in the URL and the orders have been fetched
  useEffect(() => {
    if (!orderNumber || orders.length === 0) return
    const newSelectedOrder = orders.find((o) => o.orderNumber === orderNumber)
    if (newSelectedOrder) setSelectedOrder(newSelectedOrder)
  }, [orderNumber, orders])

  return (
    <OrderHistoryContext.Provider value={{ order: selectedOrder, loading }}>
      <DashboardScreen
        loading={loading}
        error={error ? 'Could not load order history. Please try again later.' : undefined}
        headerProps={{
          showLogo: true,
          leftIcon: isMobile ? (
            <IconButton
              size="small"
              color="secondary"
              onClick={() => {
                navigate(-1)
                setMoreMenuOpen(true)
              }}
            >
              <ChevronLeft />
            </IconButton>
          ) : undefined,
        }}
        data-testid="order-history-screen"
      >
        <Grid container={true} size={12} sx={classes.container}>
          {!isMobile && (
            <StickyHeader marginBottom={1}>
              <Typography variant="body2" sx={classes.desktopHeader}>
                <HistoryOutlined /> Order history
              </Typography>
            </StickyHeader>
          )}
          <Grid container={true} size={12} sx={classes.orderHistoryContainer}>
            {orders.length === 0 && returns.length === 0 && (
              <Alert severity="info" sx={classes.noHistoryAlert}>
                No order or return history found.
              </Alert>
            )}
            {orders.map((order, index) => (
              <OrderCard key={index} order={order} hasMultipleFolders={hasMultipleFolders} />
            ))}

            {returns.map((returnItem, index) => (
              <ReturnCard key={index} returnItem={returnItem} />
            ))}
          </Grid>
        </Grid>
      </DashboardScreen>
    </OrderHistoryContext.Provider>
  )
}

export default OrderHistory
